import { Link } from "react-router-dom";
import { Lightning, MapTrifold, Scales, Bell, ShieldCheck, Truck, ArrowRight, Camera, ClipboardText, Crown } from "@phosphor-icons/react";

const FEATURES = [
  { icon: MapTrifold, title: "Real-time GPS on every wheel", body: "Live tracking from the cab phone, white-label hardware, or Samsara/PeopleNet — one truck, twenty, or two hundred. Sub-3-second updates. Geofence customer sites to auto-trigger arrival notifications." },
  { icon: Scales, title: "Federal Bridge Formula compliance", body: "49 CFR § 658.17 enforcement on every heavy-haul. Per-axle-group analysis catches the overweight permit before the trooper does. Override on file? One click and you're rolling. Owner-ops save the ticket. Fleets save the load." },
  { icon: Bell, title: "Tamper-evident POD", body: "Every drop-off photo is GPS-watermarked at capture — coords + timestamp baked into the image. Customers get a branded receipt the second the wheels stop. Disputes end before they start." },
  { icon: Camera, title: "Driver PWA + offline queue", body: "One PWA. QR-code onboarding for new drivers. Day's stops, PODs, NPS feedback — all on the phone. Queues offline through dead zones, syncs on signal. Built for the cab, not the cube." },
  { icon: ClipboardText, title: "Dispatch board + invoicing-ready PDFs", body: "Drag-style job pipeline. Hour-meter tracking. Auto-flag service-due. One-click branded daily PDFs ready for the broker, the inspector, or the customer." },
  { icon: Crown, title: "Scales from solo to multi-yard", body: "Start as an owner-op on the Starter plan. Add drivers as you grow. Hit multi-yard? Super-admin console with hard tenant isolation. Same platform, same data, no migration." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#e4e4e7]" data-testid="landing-page">
      {/* Nav */}
      <nav className="border-b border-[#2a2a2a] px-6 py-4 flex items-center justify-between sticky top-0 bg-[#0a0a0a]/95 backdrop-blur z-40">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-[#ff8c00] flex items-center justify-center">
            <Lightning size={20} weight="fill" color="#000" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight">LOAD LOGIC</span>
        </Link>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold">
          <Link to="/pricing" className="hidden md:block text-[#a1a1aa] hover:text-white">Pricing</Link>
          <Link to="/login" className="text-[#a1a1aa] hover:text-white">Sign in</Link>
          <Link to="/signup" className="px-3 py-2 bg-[#ff8c00] text-black hover:bg-[#e67e00]" data-testid="nav-signup">
            Start free trial →
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-block px-3 py-1 bg-[#ff8c00]/10 border border-[#ff8c00]/30 text-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-bold mb-5">
              · One truck. A whole fleet. Same dispatch OS.
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
              Move heavy.<br />
              Move <span className="text-[#ff8c00]">legal</span>.<br />
              Move <span className="text-[#4682b4]">paid</span>.
            </h1>
            <p className="text-base lg:text-lg text-[#a1a1aa] leading-relaxed mb-8 max-w-xl">
              The dispatch operating system for <strong className="text-white">owner-operators</strong>, <strong className="text-white">small freight fleets</strong>, and <strong className="text-white">heavy-duty rental yards</strong>.
              Live GPS, Federal Bridge compliance gating, GPS-watermarked PODs the customer can't dispute, and one-tap NPS feedback — so every load earns the paper trail it deserves.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/signup" className="px-6 py-3.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.25em] font-black flex items-center gap-2" data-testid="hero-signup">
                Start 14-day free trial <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/pricing" className="px-6 py-3.5 border border-[#2a2a2a] text-white hover:border-[#ff8c00] hover:text-[#ff8c00] text-xs uppercase tracking-[0.25em] font-black">
                See pricing
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.2em] text-[#71717a]">
              <span className="flex items-center gap-1"><ShieldCheck size={11} weight="fill" /> No card required</span>
              <span className="flex items-center gap-1"><Truck size={11} weight="fill" /> Cancel anytime</span>
              <span className="flex items-center gap-1"><Lightning size={11} weight="fill" /> Set up in under 5 min</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-5">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat label="Active trucks" value="12" trend="+3" />
                <Stat label="Live updates" value="~2s" />
                <Stat label="Geofences" value="3+" />
              </div>
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Job LL-4526 · Bridge Check</div>
                  <span className="px-2 py-0.5 bg-[#ef4444]/15 border border-[#ef4444] text-[#ef4444] text-[10px] uppercase tracking-[0.2em] font-bold">Fail</span>
                </div>
                <div className="font-mono text-xs text-[#a1a1aa] leading-relaxed">
                  Axles A2–A5 over by <span className="text-[#ef4444] font-bold">10,200 lbs</span><br />
                  Federal Bridge Formula · 5-axle semi<br />
                  <span className="text-[#71717a]">Dispatch BLOCKED until override on file</span>
                </div>
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#71717a] text-center">
                ↑ live screenshot from production
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built-for tri-persona band */}
      <section className="border-t border-[#2a2a2a] px-6 lg:px-12 py-16 max-w-7xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold mb-2 text-center">· Built for</div>
        <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tight mb-10 text-center">Whether you run one rig or a hundred, the playbook is the same.</h2>
        <div className="grid md:grid-cols-3 gap-px bg-[#2a2a2a]">
          <PersonaCard
            badge="Owner-Op"
            badgeColor="#22c55e"
            title="One truck. All the leverage."
            bullets={[
              "Bridge Formula on your phone before you accept the load",
              "GPS-watermarked POD ends shipper disputes",
              "Auto-email receipts to brokers — get paid faster",
              "Same compliance tools the big fleets pay $10k/mo for",
            ]}
            cta="Starter · $—/mo"
            testid="persona-owner-op"
          />
          <PersonaCard
            badge="Small Fleet"
            badgeColor="#ff8c00"
            title="5 to 25 trucks. Without the cube."
            bullets={[
              "Drag-style dispatch board · driver QR onboarding",
              "Push alerts on every overweight + every geofence",
              "Daily PDF reports for the broker, the customer, the books",
              "Slack alerts when a customer leaves negative feedback",
            ]}
            cta="Pro · $—/mo"
            testid="persona-small-fleet"
          />
          <PersonaCard
            badge="Rental Yard"
            badgeColor="#4682b4"
            title="Equipment rental at scale."
            bullets={[
              "Track every excavator, crane, dozer, and roll-off",
              "Hour-meter tracking · auto-flag service-due machines",
              "Multi-yard isolation · super-admin console",
              "Samsara · PeopleNet · white-label hardware-agnostic",
            ]}
            cta="Fleet · $—/mo"
            testid="persona-rental"
          />
        </div>
      </section>

      {/* Features grid */}
      <section className="border-t border-[#2a2a2a] px-6 lg:px-12 py-16 lg:py-24 max-w-7xl mx-auto">
        <div className="mb-12">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold mb-2">· Everything inside</div>
          <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tight">Every wheel on the map. Every drop on the record.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a]">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-[#0a0a0a] p-6 lg:p-8 hover:bg-[#0d0d0d] transition-colors">
              <f.icon size={28} className="text-[#ff8c00] mb-4" weight="duotone" />
              <h3 className="font-heading font-bold text-lg tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-[#a1a1aa] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#2a2a2a] px-6 lg:px-12 py-16 max-w-3xl mx-auto text-center">
        <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tight mb-4">14 days free. No card. Cancel any time.</h2>
        <p className="text-[#a1a1aa] mb-8">Sign up and your demo yard is already running. Owner-ops, small freight fleets, and rental yards all start the same way — slot in your iron and roll in under 5 minutes.</p>
        <Link to="/signup" className="inline-block px-8 py-4 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.25em] font-black">
          Get started — it's free
        </Link>
      </section>

      <footer className="border-t border-[#2a2a2a] px-6 py-6 text-center text-[10px] uppercase tracking-[0.25em] text-[#71717a]">
        Load Logic · Dispatch Operating System · © {new Date().getFullYear()}
      </footer>
    </div>
  );
}

function Stat({ label, value, trend }) {
  return (
    <div className="bg-[#1a1a1a] py-3 px-2 text-center">
      <div className="font-mono font-bold text-xl text-white">{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a] mt-1">{label}</div>
      {trend && <div className="text-[9px] uppercase tracking-[0.2em] text-[#22c55e] mt-0.5">{trend}</div>}
    </div>
  );
}

function PersonaCard({ badge, badgeColor, title, bullets, cta, testid }) {
  return (
    <div className="bg-[#0a0a0a] p-6 lg:p-8" data-testid={testid}>
      <div className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] font-black mb-3" style={{ background: badgeColor, color: "#000" }}>
        {badge}
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-4">{title}</h3>
      <ul className="space-y-2 mb-5">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#a1a1aa] leading-relaxed">
            <span className="text-[#ff8c00] mt-0.5">▸</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold pt-4 border-t border-[#2a2a2a]" style={{ color: badgeColor }}>
        {cta}
      </div>
    </div>
  );
}
